<template>
  <div
    ref="rootRef"
    class="voice-waveform flex h-7 min-w-0 w-full flex-1 cursor-pointer items-center overflow-hidden"
    data-test="voice-waveform"
    @click.stop="onSeek"
  >
    <span
      v-for="(peak, index) in bars"
      :key="index"
      class="voice-waveform__bar"
      :class="index < playedBarCount ? 'voice-waveform__bar--played' : 'voice-waveform__bar--idle'"
      :style="{ height: `${Math.max(2, Math.round(peak * 28))}px` }"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import { WAVEFORM_BARS_COUNT } from '../model/constants'

const BAR_WIDTH_PX = 2
const BAR_GAP_PX = 1

type Props = {
  peaks: number[]
  progress?: number
  isPlaying?: boolean
  barCount?: number
}

const props = withDefaults(defineProps<Props>(), {
  progress: 0,
  isPlaying: false,
  barCount: WAVEFORM_BARS_COUNT,
})

const emit = defineEmits<{
  (event: 'seek', ratio: number): void
}>()

const rootRef = ref<HTMLElement | null>(null)
const containerWidth = ref(0)
let resizeObserver: ResizeObserver | null = null

const fittedBarCount = computed(() => {
  if (!containerWidth.value) return props.barCount
  return Math.max(1, Math.floor((containerWidth.value + BAR_GAP_PX) / (BAR_WIDTH_PX + BAR_GAP_PX)))
})

const playedBarCount = computed(() => {
  const count = fittedBarCount.value
  if (props.progress > 0) return Math.min(count, Math.max(1, Math.ceil(props.progress * count)))
  return props.isPlaying ? 1 : 0
})

const bars = computed(() => {
  const count = fittedBarCount.value
  const peaks = props.peaks

  if (!peaks.length) return Array.from({ length: count }, () => 0.08)
  if (peaks.length === count) return peaks

  return Array.from({ length: count }, (_, index) => {
    if (peaks.length > count) {
      const start = Math.floor((index * peaks.length) / count)
      const end = Math.max(start + 1, Math.floor(((index + 1) * peaks.length) / count))
      let peak = 0
      for (let sampleIndex = start; sampleIndex < end; sampleIndex += 1) {
        peak = Math.max(peak, peaks[sampleIndex] ?? 0)
      }
      return peak
    }

    if (peaks.length === 1) return peaks[0] ?? 0.08

    const position = (index * (peaks.length - 1)) / Math.max(1, count - 1)
    const left = Math.floor(position)
    const right = Math.min(peaks.length - 1, left + 1)
    const t = position - left
    return (peaks[left] ?? 0) * (1 - t) + (peaks[right] ?? 0) * t
  })
})

const onSeek = (event: MouseEvent) => {
  const target = (rootRef.value ?? event.currentTarget) as HTMLElement
  const rect = target.getBoundingClientRect()
  if (!rect.width) return

  const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width))
  emit('seek', ratio)
}

onMounted(() => {
  if (!rootRef.value || typeof ResizeObserver === 'undefined') return

  resizeObserver = new ResizeObserver((entries) => {
    containerWidth.value = entries[0]?.contentRect.width ?? 0
  })
  resizeObserver.observe(rootRef.value)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
})
</script>

<style scoped>
.voice-waveform {
  gap: 1px;
}

.voice-waveform__bar {
  display: block;
  width: 2px;
  flex-shrink: 0;
  border-radius: 9999px;
}

.voice-waveform__bar--played {
  background: var(--color-surf-brand-base-def, #1456b8);
}

.voice-waveform__bar--idle {
  background: #bfd6f8;
}
</style>
